"use client"

import { useEffect, useState } from "react"
import { useObscuredByZone } from "@/hooks/use-obscured-by-zone"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useNavTheme } from "@/hooks/use-nav-theme"
import { isForceDarkBooking, isMarketingContentPath, isGlassNavPath, isChromelessPath, stripLocalePrefix } from "@/i18n/routing"
import { useUserRole } from "@/providers/user-role-provider"

// Tiny BOOTH wordmark fixed at the top-center of every page.
// Bare logo (no pill, no shadow, no border) — sits transparently on
// the page chrome. Fades out as soon as the user starts scrolling so
// it doesn't compete with content. Listens at document capture so
// it picks up nested scroll containers (e.g. /listings inner panel)
// in addition to the window.
//
// Was mobile-only until DesktopFloatingNav was removed; that component
// carried its own top-centre wordmark in the same slot, but a dumber one —
// hardcoded to /dashboard, no light/dark swap, no booking-funnel handling.
// This one absorbed the job rather than being duplicated for a second time.

// Prefix-based hide list. `/auth/*` covers login, signup, onboarding,
// verify-email, etc. — auth pages render their own focused chrome and
// shouldn't have the global wordmark floating over the form.
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/claim", "/clients", "/preview"]
// Marketing pages hide outright: the glass nav pill carries the wordmark dead
// centre, so this floating one would double up at the top of the same screen.

function shouldHide(pathname: string | null): boolean {
  if (!pathname) return false
  // Pages the glass nav owns carry the whole site in their menu panel — this
  // would be a second, competing one on the same screen.
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

const SCROLL_FADE_AT = 32 // px — small lift before fading

export function BrandHeader() {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  // Auth-aware home target. Logged-in users tapping the wordmark should
  // land on their dashboard, NOT the marketing landing — routing them to
  // "/" makes the public page paint for a beat before the client-side
  // redirect fires (the "flash" of marketing). Sending them straight to
  // their role dashboard skips "/" entirely. Logged-out users still go
  // to the marketing landing.
  const { userId, role, actualUserType, isClient } = useUserRole()
  const resolvedRole = userId ? (role ?? actualUserType) : null
  // On the public client booking funnel (/book/*), the logo keeps the client on
  // the barber's booking page instead of yanking them into the pro app.
  const inBookingFunnel = pathname?.startsWith("/book/")
  // The client booking storefront (/book/[slug] + /book/quick/[slug]) is force-
  // dark for everyone, so the wordmark must use the WHITE logo there. This header
  // sits outside the .force-dark wrapper, so the CSS logo swap (which keys on the
  // ancestor) can't reach it — render the white wordmark directly instead.
  const forceDarkLogo = isForceDarkBooking(pathname)
  // A pro in CLIENT MODE (on /dashboard/client) should keep landing there when
  // they tap the wordmark, not bounce back to their pro dashboard.
  const inClientMode = pathname?.startsWith("/dashboard/client")
  const homeHref = inBookingFunnel
    ? pathname!
    : !userId
      ? "/"
      : (isClient || inClientMode)
        ? "/dashboard/client"
        : resolvedRole === "owner"
          ? "/dashboard/owner"
          : "/dashboard/barber"
  // Landing's first section is the dark hero, so default to dark there.
  // Everywhere else defaults to light (matches the off-white app bg).
  const theme = useNavTheme(pathname === "/" ? "dark" : "light")
  // White-text logo when the surface is dark. Two cases:
  //   1. A dark page *section* sits behind the logo (navTheme) — JS, light mode.
  //   2. App-wide dark mode — driven by the `html.dark` class via CSS below so
  //      it paints instantly (no JS resolvedTheme flash on the dashboard).
  const navDark = theme === "dark"

  // Marketing content pages don't follow the app's dark mode — they're light
  // (or declare their own data-nav-theme) whatever the viewer's theme is. That
  // matters twice over:
  //
  //   1. PublicNavbar stays up on them now, so this wordmark would be a second
  //      lockup on desktop. It steps aside there, same as it does signed-out.
  //   2. The logo swap below is CSS keyed on html.dark — the APP's theme. On a
  //      page that ignores the app theme, a dark-mode viewer got the white
  //      wordmark on a light page, which is how /press ended up invisible.
  //      Here the variant is chosen in JS from the surface instead.
  const publicSurface = isMarketingContentPath(pathname)

  // The dashboard keeps its wordmark at all times.
  //
  // The scroll fade exists so the mark doesn't compete with long marketing
  // copy, but the dashboard is the app's own chrome — there it just meant the
  // logo vanished the moment you moved and only came back if you scrolled all
  // the way up to fetch it, which reads as the header being broken rather than
  // considerate. Marketing pages keep the fade.
  //
  // /listings is the same case for someone signed in: it is the app's browse
  // surface, not marketing copy, and the fade there read as the logo going
  // missing. Signed out it keeps the fade, because PublicNavGlass carries its
  // own wordmark on that page.
  //
  // /hiring is the same browse surface. On desktop its listing column is its
  // own scroller while the page stays put, so a faded wordmark never came
  // back until the column was scrolled to the top again.
  const appPath = pathname ? stripLocalePrefix(pathname) : ""
  const pinned =
    appPath.startsWith("/dashboard") ||
    (!!userId && (appPath.startsWith("/listings") || appPath.startsWith("/hiring")))

  useEffect(() => {
    setScrolled(false)
    const handler = (e: Event) => {
      const t = e.target
      let st = 0
      if (t instanceof Document) {
        st = window.scrollY
      } else if (t instanceof HTMLElement) {
        // Only vertical scrollers get a say. The listing cards' photo
        // carousels and the filter chip rows scroll sideways, and every swipe
        // on one reported scrollTop 0 here — which un-faded the wordmark
        // halfway down the page, then the next page scroll faded it again.
        // The logo blinked with each swipe and looked like it was missing.
        if (t.scrollHeight <= t.clientHeight) return
        st = t.scrollTop
      }
      setScrolled(st > SCROLL_FADE_AT)
    }
    // capture:true so nested scrollers (overflow-y:auto containers)
    // still trigger the fade — scroll doesn't bubble by default.
    document.addEventListener("scroll", handler, { capture: true, passive: true })
    return () => document.removeEventListener("scroll", handler, { capture: true } as EventListenerOptions)
  }, [pathname])

  /**
   * The wordmark's own footprint — top-6 (24px) plus h-5 (20px), with a few px
   * of slack either side so the fade starts as the section's edge arrives
   * rather than after it has already cut across the letters.
   *
   * On the dashboard this header is `pinned`, meaning it never fades on scroll
   * — which is right almost everywhere and wrong over the floor plan, where the
   * wordmark ghosted through the section's own "N booths with availability"
   * header and read as a rendering fault.
   */
  const obscured = useObscuredByZone("top", 16, 56) !== "none"

  if (shouldHide(pathname)) return null

  return (
    <div
      // top-3.5 on the phone: 14px + half the 20px mark = the 24px centre
      // line the top-left pill and the bell sit on. Desktop keeps top-6.
      className={`fixed safe-top-mark left-1/2 -translate-x-1/2 z-40 pointer-events-none transition-opacity duration-200 ${
        // Signed-out desktop keeps PublicNavbar, which carries its own brand
        // lockup at the top-left — a second wordmark centred above it reads as
        // a mistake. Mobile shows both today and is left alone.
        userId && !publicSurface ? "" : "md:hidden"
      } ${(scrolled && !pinned) || obscured ? "opacity-0" : "opacity-100"}`}
      // Slow out, quicker back: leaving should read as the wordmark dissolving
      // into the section rather than blinking off.
      style={{ transitionDuration: obscured ? "420ms" : "260ms" }}
      // An invisible link that still takes focus and still answers a screen
      // reader is worse than no link.
      aria-hidden={obscured}
      inert={obscured ? true : undefined}
    >
      <Link
        href={homeHref}
        className="pointer-events-auto inline-flex items-center justify-center"
        aria-label="BOOTH home"
      >
        {forceDarkLogo || (publicSurface && navDark) ? (
          /* Force-dark booking storefront: always the white wordmark, no
             html.dark swap (the header is outside the .force-dark wrapper, so
             the CSS swap that keys on the ancestor can't reach it). */
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src="/images/booth-wordmark-dark.webp" alt="BOOTH" className="h-5 w-auto" />
        ) : publicSurface ? (
          /* Light surface that ignores the app theme — the black wordmark,
             decided here rather than by the html.dark CSS swap. */
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src="/images/booth-wordmark-light.webp" alt="BOOTH" className="h-5 w-auto" />
        ) : (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/booth-wordmark-light.webp"
              alt="BOOTH"
              className={`h-5 w-auto ${navDark ? "hidden" : "logo-light"}`}
            />
            {/* `hidden` keeps the dark variant hidden by default (always compiles);
                html.dark .logo-dark reveals it — so two logos can never both show. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/booth-wordmark-dark.webp"
              alt="BOOTH"
              className={`h-5 w-auto ${navDark ? "" : "hidden logo-dark"}`}
            />
          </>
        )}
      </Link>
    </div>
  )
}
